import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import ProfileContext from "./ProfileContext";
import useUsers from "../../hooks/useUsers";
import { PAGE_STATE, WORK_PROGRAMS, WORK_RENDERERS } from "../../constants";
import AppContext from "../../AppContext";
import List, { STATE } from "../../components/List";
import SecondaryPagination from "../../components/SecondaryPagination/SecondaryPagination";
import useLikes from "../../hooks/useLikes";
import useWorks from "../../hooks/useWorks";
import useScreenSize from "../../hooks/useScreenSize";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ReactComponent as UnlikeSvg } from "../../assets/icons/liked-primary.svg";
import { ReactComponent as ShareSvg } from "../../assets/icons/share-primary.svg";
import * as Tooltip from "react-tooltip";
import { toast } from "react-toastify";
import Share from "./ShareProject";
import useJournals from "../../hooks/useJournals";
import useCompanies from "../../hooks/useCompanies";
import { de } from "date-fns/locale";
import _ from "lodash";
import { format } from "date-fns";

const assetsUri = import.meta.env.ASSETS_URI;

const Purchases = () => {
  const { plans, viewProject, removeLikes } = useContext(AppContext);
  const { user, company, isCompany } = useContext(ProfileContext);
  const { queryUsers, queryPurchases } = useUsers();
  const { queryCompany, queryPurchases: queryCompanyPurchases } =
    useCompanies();
  const { queryLikes } = useLikes();
  const { queryWorks } = useWorks();
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [likes, setLikes] = useState(undefined);
  const [work, setWork] = useState(undefined);
  const { screenSize } = useScreenSize();
  const toggleMobile = ["xs", "sm"].includes(screenSize);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [metadata, setMetadata] = useState(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const [shareProject, setShareProject] = useState(undefined);

  const columns = useMemo(() => {
    if (isCompany)
      return [
        {
          display: "Project",
          key: "project",
          textAlign: "left",
        },
        {
          display: "Purchased By",
          key: "purchasedBy",
          textAlign: "left",
        },
        {
          display: "Purchased On",
          key: "purchasedOn",
          textAlign: "left",
          width: "130px",
        },
      ];

    return [
      {
        display: "Project",
        key: "project",
        textAlign: "left",
      },
      {
        display: "Purchased On",
        key: "purchasedOn",
        textAlign: "left",
        width: "130px",
      },
    ];
  }, [isCompany]);

  useEffect(() => {
    fetchData();
  }, [user, isCompany, company, page, limit]);

  const fetchData = async () => {
    let _purchases = [];
    let metadata = {};

    if (isCompany && !_.isEmpty(company)) {
      const { data: journals, _metadata } = await queryCompanyPurchases(
        company._id,
        {},
        page,
        limit
      );

      _purchases = journals;
      metadata = _metadata;
    }

    if (!isCompany && !_.isEmpty(user)) {
      const { data: journals, _metadata } = await queryPurchases(
        user._id,
        {},
        page,
        limit
      );

      _purchases = journals;
      metadata = _metadata;
    }

    if (_purchases.length === 0) {
      setDataState(PAGE_STATE.IDLE);
      return;
    }

    let userIds = [];
    let companyIds = [];

    for (const purchase of _purchases) {
      userIds.push(purchase.purchasedBy);

      if (purchase.company) {
        companyIds.push(purchase.company);
        continue;
      }

      userIds.push(purchase.createdBy);
    }

    const { data: users } = await queryUsers(
      {
        _id: {
          $in: userIds,
        },
      },
      null,
      -1,
      { avatar: 1, firstname: 1, surname: 1 }
    );

    const { data: companies } = await queryCompany(
      {
        _id: {
          $in: companyIds,
        },
      },
      null,
      -1,
      { logo: 1, name: 1 }
    );

    let _dataSource = _purchases.map((_work) => {
      let purchasor = users.find((u) => u._id === _work.purchasedBy);

      const _unavailable = !_work.active || _work.deleted;

      return {
        ..._work,
        workId: _work._id,
        type: _work.type,
        project: (
          <div className="profile-purchases-list-project">
            <img
              className={`profile-purchases-list-project__banner ${
                _unavailable
                  ? "profile-purchases-list-project__banner--inactive"
                  : ""
              }`}
              alt="work-banner"
              src={`${assetsUri}/${_work.banner}`}
              onError={({ target }) => {
                target.src = _work.banner;
              }}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                className={`profile-purchases-list-project__name ${
                  _unavailable
                    ? "profile-purchases-list-project__name--inactive"
                    : ""
                }`}
              >
                {_work.name}
              </div>
              {_unavailable && (
                <span className="profile-purchases-list-project__alert">
                  Unvailable
                </span>
              )}
            </div>
          </div>
        ),
        purchasedBy: (
          <div className="profile-purchases-list-user">
            <img
              className="profile-purchases-list-user__avatar"
              alt="follower-avatar"
              src={`${assetsUri}/${purchasor.avatar}`}
              onError={({ target }) => {
                target.src = purchasor.avatar;
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <div className="profile-purchases-list-user__name">
                {purchasor.firstname} {purchasor.surname || ""}
              </div>
            </div>
          </div>
        ),
        purchasedOn: format(new Date(_work.purchasedOn), "dd/MM/yyyy HH:mm"),
      };
    });

    setLikes(_dataSource.filter((l) => !!l));
    setMetadata(metadata);
    setDataState(STATE.IDLE);
  };

  const handleViewClick = (workId, type) => {
    // * Skip viewing project if unavailable
    const _work = likes.find((l) => l._id === workId);

    if (!_work.active || _work.deleted) {
      toast.warn("Project is unvailable");
      return;
    }

    if (type === 1 || type === 3) {
      viewProject(workId, location);
      return;
    }

    navigate(`/work-exchanges/${workId}`);
  };

  return (
    <div
      className={`profile-purchases ${
        toggleMobile ? "profile-purchases--mobile" : ""
      }`}
    >
      <List
        className="profile-purchases-list"
        contentState={dataState}
        columns={columns}
        dataSource={likes}
        scrollable={true}
        viewable={true}
        onRecordClick={({ workId, type }) => {
          handleViewClick(workId, type);
        }}
      >
        <div>
          <SecondaryPagination
            limits={[10, 50]}
            defaultPage={metadata?.currentPage ?? 1}
            defaultLimit={limit}
            pages={metadata?.totalPages ?? 1}
            onPageChange={(page) => setPage(page)}
            onLimitChange={(limit) => setLimit(limit)}
          />
        </div>
      </List>
      <Tooltip.Tooltip id="tooltip-detail" />
      {shareProject && (
        <Share link={shareProject} onClose={() => setShareProject(undefined)} />
      )}
    </div>
  );
};

Purchases.propTypes = {};

export default Purchases;
